import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as the
// other showcases' local COMMON_DIFFICULTY.
const COMMON_DIFFICULTY = 0;

/**
 * A wall built the way a jar can actually carry load: cube, disc, cube, disc,
 * cube, with a full-height post on either side of every stack.
 *
 * A disc is a cylinder lying on its side, so on its own it is a roller and
 * anything resting on it is balanced rather than supported — measured on the
 * jar-cases bench, a lone cube on a lone disc slid 0.30 of a cube and a disc on
 * a disc rolled off its perch in half the runs. The posts are what makes the
 * pattern work: they stand the full height of the stack in the neighbouring
 * columns, so the only direction a disc could roll is already blocked, a cell
 * gap away.
 *
 * Columns alternate POST, stack, stack, POST, stack, stack, POST. Two stacks
 * share each pair of posts, which is what the reference art does too.
 */

const CUBE = 2 as LevelPieceType;
const JAR = 3 as LevelPieceType;

const COLS = 7;
const ROWS = 5;

/** Columns that hold a single full-height post instead of a stack. */
const POST_COLUMNS: readonly number[] = [ 0, 3, 6 ];

function isPost( col: number ): boolean
{
  return POST_COLUMNS.includes( col );
}

function makeLayer(): LevelLayer
{
  const grid = Array.from( { length: ROWS }, () => Array( COLS ).fill( 0 ) );
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let col = 0; col < COLS; col++ )
  {
    if ( isPost( col ) )
    {
      // One piece spanning the whole height, so the post cannot come apart at a
      // joint the way a column of loose cubes would.
      for ( let row = 0; row < ROWS; row++ )
        grid[row][col] = id;

      pieces.push( { id, type: CUBE, x: col, y: 0, width: 1, height: ROWS, variant: 0 } );
      id++;
      continue;
    }

    // Odd rows are the discs, so the stack reads cube, disc, cube, disc, cube
    // from the table up — every jar sandwiched between two cubes.
    for ( let row = 0; row < ROWS; row++ )
    {
      const is_disc = row === 2;

      grid[row][col] = id;
      pieces.push( {
        id,
        type: is_disc ? JAR : CUBE,
        x: col,
        y: row,
        width: 1,
        height: 1,
        variant: 0,
        ...( is_disc ? { lying: true } : {} ),
      } );
      id++;
    }
  }

  return { grid, pieces };
}

const JAR_SANDWICH_LEVEL: LevelConfig = {
  level: 90311,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  layers: [ makeLayer() ],
};

export class JarSandwichShowcase
{
  public static readonly LEVEL = JAR_SANDWICH_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? JAR_SANDWICH_LEVEL : null;
  }
}
